import React, { useMemo, useState } from 'react';
import { Calendar, BarChart3, TrendingUp, Percent, DollarSign, Settings2, Info } from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';

// Seasonality & Demand Forecast (Demo)
// - Dummy monthly data: 12 months history + 6 months forecast
// - Simple seasonal + trend generator, plus confidence bands
// - Scenario sliders: price %, promo %, extra spend

const CATEGORIES = [
  { id: 'footwear', name: 'Footwear', baseUnits: 12000, aov: 750, growth: 0.012, seasonAmp: 0.18, elasticity: -1.2, incRoas: 3.0 },
  { id: 'apparel', name: 'Apparel', baseUnits: 18000, aov: 420, growth: 0.009, seasonAmp: 0.22, elasticity: -0.9, incRoas: 2.5 },
  { id: 'accessories', name: 'Accessories', baseUnits: 8000, aov: 280, growth: 0.015, seasonAmp: 0.25, elasticity: -1.6, incRoas: 3.5 },
];

const MONTH_LABELS = (() => {
  const now = new Date();
  const arr = [];
  for (let i = 17; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({ key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) });
  }
  return arr;
})();

function seedRand(seed) {
  let t = seed | 0;
  return () => {
    t ^= t << 13; t ^= t >> 17; t ^= t << 5;
    return Math.abs(t) / 0x7fffffff;
  };
}

function genCategorySeries(cat) {
  const rnd = seedRand(cat.baseUnits);
  const series = [];
  let unitsLevel = cat.baseUnits;
  for (let i = 0; i < MONTH_LABELS.length; i++) {
    const { key, label } = MONTH_LABELS[i];
    const t = i;
    const seasonal = 1 + cat.seasonAmp * Math.sin((2 * Math.PI * t) / 12);
    const trend = Math.pow(1 + cat.growth, t - 12); // roughly stable over last year, slight growth into forecast
    const noise = 1 + (rnd() - 0.5) * 0.08;
    const units = Math.max(0, unitsLevel * seasonal * trend * noise);
    const aovAdj = 1 + (rnd() - 0.5) * 0.05;
    const aov = cat.aov * aovAdj;
    const revenue = units * aov;
    series.push({ key, label, units: Math.round(units), revenue: Math.round(revenue), aov: Math.round(aov) });
    // drift units level slightly
    unitsLevel = unitsLevel * (1 + (rnd() - 0.5) * 0.04);
  }
  return series;
}

function decomposeSeasonality(series) {
  // Simple decomposition: estimate seasonal index by month-of-year average ratio
  const byMonth = Array.from({ length: 12 }, () => []);
  series.slice(0, 12).forEach((p, idx) => {
    const m = (new Date(MONTH_LABELS[idx].key + '-01')).getMonth();
    byMonth[m].push(p.units);
  });
  const monthAvg = byMonth.map(arr => (arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 1));
  const overallAvg = series.slice(0, 12).reduce((s,p)=>s+p.units,0) / 12;
  const seasonalIdx = monthAvg.map(v => (v/overallAvg));
  return seasonalIdx;
}

function computeBands(history, forecastHorizon = 6) {
  // residual std from last 12 months as noise proxy
  const vals = history.slice(-12).map(p => p.units);
  const mean = vals.reduce((a,b)=>a+b,0)/vals.length;
  const variance = vals.reduce((s,v)=>s+Math.pow(v-mean,2),0)/Math.max(1,(vals.length-1));
  const sigma = Math.sqrt(Math.max(variance, 1));
  const p50 = 0;
  const p80z = 0.84; // approx
  const p95z = 1.64;
  return { sigma, z: { p50, p80: p80z, p95: p95z } };
}

function formatMoneyTRY(x){ return '₺' + Math.round(x).toLocaleString('tr-TR'); }

export default function SeasonalityForecastDemo(){
  const base = useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => { map[c.id] = { meta: c, series: genCategorySeries(c) }; });
    return map;
  }, []);

  const [selectedId, setSelectedId] = useState(CATEGORIES[0].id);
  const [pricePct, setPricePct] = useState(0);     // -30 .. +30
  const [promoPct, setPromoPct] = useState(0);     // 0 .. 50
  const [extraSpend, setExtraSpend] = useState(0); // 0 .. 500k

  const selected = base[selectedId];

  const adjusted = useMemo(() => {
    const cat = selected.meta;
    const elastic = cat.elasticity;
    const incRoas = cat.incRoas;
    const series = selected.series.map((p, idx) => {
      const isForecast = idx >= 12; // last 6 points are "forecast horizon"
      let units = p.units;
      let aov = p.aov;
      // price effect on units: units' = units * (1 + E * Δp)
      const dp = pricePct / 100;
      units = Math.max(0, units * (1 + elastic * dp));
      aov = Math.max(1, aov * (1 + dp));
      // promo uplift on units in forecast horizon only
      const promoUplift = isForecast ? (promoPct / 100) * 0.5 : 0; // 50% passthrough of discount to volume
      units = Math.max(0, units * (1 + promoUplift));
      // extra spend incremental revenue spread across forecast months evenly
      let revenue = units * aov;
      if (isForecast && extraSpend > 0) {
        const spread = extraSpend / 6;
        revenue += spread * incRoas; // incremental rev = spend × incRoas
      }
      return { ...p, isForecast, units: Math.round(units), aov: Math.round(aov), revenue: Math.round(revenue) };
    });
    return series;
  }, [selected, pricePct, promoPct, extraSpend]);

  const seasonalIdx = useMemo(()=>decomposeSeasonality(selected.series), [selected]);
  const bands = useMemo(()=>computeBands(selected.series), [selected]);

  const forecastChartData = useMemo(() => {
    return adjusted.map(p => {
      // derive bands around units and revenue for forecast part
      const baseUnits = p.units;
      const spread80 = p.isForecast ? bands.z.p80 * bands.sigma : 0;
      const spread95 = p.isForecast ? bands.z.p95 * bands.sigma : 0;
      const u80hi = Math.round(baseUnits + spread80);
      const u80lo = Math.max(0, Math.round(baseUnits - spread80));
      const u95hi = Math.round(baseUnits + spread95);
      const u95lo = Math.max(0, Math.round(baseUnits - spread95));
      return {
        date: p.label,
        units: baseUnits,
        revenue: p.revenue,
        u80hi, u80lo, u95hi, u95lo
      };
    });
  }, [adjusted, bands]);

  const next3 = adjusted.slice(12, 15);
  const next3UnitsP50 = next3.reduce((s,p)=>s+p.units,0);
  const next3RevP50 = next3.reduce((s,p)=>s+p.revenue,0);
  const promoWindows = adjusted.slice(12).filter(p => {
    const m = (new Date(p.key + '-01')).getMonth();
    return (seasonalIdx[m] || 1) > 1.05; // above average seasonality
  }).map(p => p.label);

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Seasonality & Demand Forecast (Demo)</h1>
            <p className="text-sm text-gray-500 mt-1">Category‑level 12m history + 6m forecast with simple seasonal model and scenarios</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className="border border-gray-200 rounded px-3 py-2 text-sm" value={selectedId} onChange={(e)=>setSelectedId(e.target.value)}>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Next 3 Months — Units (P50)</div>
          <div className="text-2xl font-bold text-gray-800">{next3UnitsP50.toLocaleString('en-US')}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Next 3 Months — Revenue (P50)</div>
          <div className="text-2xl font-bold text-gray-800">{formatMoneyTRY(next3RevP50)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Suggested Promo Windows</div>
          <div className="text-sm text-gray-800">{promoWindows.slice(0,3).join(' · ') || '—'}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Notes</div>
          <div className="text-sm text-gray-700 flex items-start gap-2"><Info className="w-4 h-4 text-gray-400" />Simple demo bands (P80/P95) from residual std; real model would use SARIMA/Prophet.</div>
        </div>
      </div>

      {/* Scenario Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-gray-500" />
            <h3 className="font-medium text-gray-800 text-sm">Scenarios</h3>
          </div>
          <div className="text-xs text-gray-500">Price, discount and extra spend apply to forecast months</div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Percent className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700 w-28">Price change</span>
            <input type="range" min={-30} max={30} step={1} value={pricePct} onChange={(e)=>setPricePct(parseInt(e.target.value))} className="w-full" />
            <span className="text-sm text-gray-800 w-10 text-right">{pricePct}%</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700 w-28">Promo discount</span>
            <input type="range" min={0} max={50} step={1} value={promoPct} onChange={(e)=>setPromoPct(parseInt(e.target.value))} className="w-full" />
            <span className="text-sm text-gray-800 w-10 text-right">{promoPct}%</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700 w-28">Extra spend</span>
            <input type="range" min={0} max={500000} step={10000} value={extraSpend} onChange={(e)=>setExtraSpend(parseInt(e.target.value))} className="w-full" />
            <span className="text-sm text-gray-800 w-24 text-right">{formatMoneyTRY(extraSpend)}</span>
          </div>
        </div>
      </div>

      {/* Forecast Chart with Bands */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb  -3">
          <h3 className="font-medium text-gray-800 text-sm">Forecast vs History — {selected.meta.name}</h3>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={forecastChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorBand" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#bfdbfe" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#3b82f6" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#22c55e" />
              <Tooltip
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value, name) => {
                  if (name === 'Units') return [value, 'Units'];
                  if (name === 'Revenue') return [formatMoneyTRY(value), 'Revenue'];
                  return [value, name];
                }}
              />
              <Legend />
              {/* P80 band */}
              <Area type="monotone" dataKey="u80hi" stroke="none" fill="url(#colorBand)" yAxisId="left" />
              <Area type="monotone" dataKey="u80lo" stroke="none" fill="#fff" yAxisId="left" />
              <Line type="monotone" dataKey="units" name="Units" stroke="#3b82f6" strokeWidth={2} yAxisId="left" dot={false} />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#22c55e" strokeWidth={2} yAxisId="right" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">Category Plan — Next 3 Months</h3>
          <div className="text-xs text-gray-500">P50 with simple bands; budget band ≈ 8–12% of revenue</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium py-2 pr-4">Category</th>
                <th className="text-right font-medium py-2 pr-4">Units P50</th>
                <th className="text-right font-medium py-2 pr-4">Revenue P50</th>
                <th className="text-right font-medium py-2 pr-4">Budget Band</th>
                <th className="text-right font-medium py-2">Promo Windows</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map(cat => {
                const ser = base[cat.id].series;
                const seasonal = decomposeSeasonality(ser);
                const adj = ser.map((p, idx) => {
                  const isForecast = idx >= 12;
                  let units = p.units;
                  let aov = p.aov;
                  if (isForecast) {
                    const dp = pricePct/100;
                    units = Math.max(0, units * (1 + cat.elasticity * dp) * (1 + (promoPct/100)*0.5));
                    aov = Math.max(1, aov * (1 + dp));
                  }
                  return { ...p, isForecast, units, aov, revenue: units * aov };
                });
                const next = adj.slice(12, 15);
                const unitsP50 = Math.round(next.reduce((s,p)=>s+p.units,0));
                const revP50 = Math.round(next.reduce((s,p)=>s+p.revenue,0));
                const budgetMin = Math.round(revP50 * 0.08);
                const budgetMax = Math.round(revP50 * 0.12);
                const promos = adj.slice(12).filter(p => {
                  const m = (new Date(p.key + '-01')).getMonth();
                  return (seasonal[m] || 1) > 1.05;
                }).map(p => p.label).slice(0,3).join(' · ');
                return (
                  <tr key={cat.id} className="border-top border-gray-100">
                    <td className="py-2 pr-4 text-gray-800">{cat.name}</td>
                    <td className="py-2 pr-4 text-right text-gray-800">{unitsP50.toLocaleString('en-US')}</td>
                    <td className="py-2 pr-4 text-right text-gray-800">{formatMoneyTRY(revP50)}</td>
                    <td className="py-2 pr-4 text-right text-gray-800">{formatMoneyTRY(budgetMin)} – {formatMoneyTRY(budgetMax)}</td>
                    <td className="py-2 text-right text-gray-800">{promos || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


